import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useParallaxRef } from '@/utils/parallax';
import { useRevealOnce } from '@/hooks/useRevealOnce';
import { gridContainerVariants, getGridItemVariants } from '@/lib/animations';
import { useGradientBackground } from '@/utils/gradientBackground';
import { packages } from '@/components/ServicesPackagesSection';

// Card names for the three service packages; price and description come from ServicesPackagesSection
const PACKAGE_CARD_TITLES: Record<string, string> = {
  Starter: 'Branding',
  Basic: 'Website and Branding',
  Pro: 'Complete Package',
};

export default function ServicesCategorySection() {
  const backgroundStyle = useGradientBackground();

  // Parallax refs
  const titleRef = useParallaxRef({ speed: -0.12, axis: 'y', maxTranslate: 70 });
  const subtitleRef = useParallaxRef({ speed: -0.18, axis: 'y', maxTranslate: 64 });

  // Reveal hook
  const sectionReveal = useRevealOnce({ amount: 0.2 });

  return (
    <section className="py-24" style={backgroundStyle}>
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16 stagger-fade-1">
            <h2
              ref={titleRef as any}
              className="text-6xl md:text-7xl font-bold mb-8 liquid-gradient-text drop-shadow-lg"
            >
              3 Service Packages
            </h2>
            <p
              ref={subtitleRef as any}
              className="text-3xl md:text-4xl text-foreground/70 max-w-4xl mx-auto leading-relaxed font-normal"
            >
              Get the help you need
            </p>
          </div>

          <motion.div
            ref={sectionReveal.ref}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
            variants={gridContainerVariants}
            initial="hidden"
            animate={sectionReveal.controls}
          >
            {packages.map((pkg, index) => (
              <motion.div key={pkg.title} variants={getGridItemVariants(index)}>
                <Link to="/contact" className="block group">
                  <div className="apple-card p-6 h-full transition-all duration-300 group-hover:scale-105">
                    <div className="mb-4">
                      <h3 className="text-xl font-bold text-black group-hover:text-primary transition-all duration-300">
                        {PACKAGE_CARD_TITLES[pkg.title]}
                      </h3>
                      <p className="text-muted-foreground text-sm">From {pkg.price}</p>
                    </div>
                    <p className="text-muted-foreground text-sm leading-relaxed">
                      {pkg.oneLiner}
                    </p>
                  </div>
                </Link>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </div>
    </section>
  );
}

